import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Badge, Col, Container, Row } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { fetchKitchensPage } from "../../services/catalog.api";
import KitchenCard from "../home/cards/KitchenCard";
import ErrorInfo from "../shared/ErrorInfo";
import LoadMore from "../shared/LoadMore";
import NodataFound from "../shared/NodataFound";

export default function AllKitchens() {
  const {
    data: pages,
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["all-kitchens"],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchKitchensPage({ page: pageParam, limit: 24 }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
  });

  const data = useMemo(
    () => (pages?.pages || []).flatMap((p) => p.items),
    [pages],
  );
  const total = pages?.pages?.[0]?.pagination?.total ?? data.length;

  if (error) {
    toast.error(error?.message);
    return (
      <ErrorInfo
        message={error?.message}
        details={error?.response?.data?.data}
      />
    );
  }

  if (!isPending && data?.length === 0) {
    return <NodataFound />;
  }

  return (
    <div className="container page-navbar-space">
      <div className="bg-primary bg-gradient rounded-4 p-4 p-md-5 mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h3 className="text-white fw-bold mb-2">
              Explore Popular Kitchens
            </h3>
            <p className="text-white mb-0">
              Find trusted kitchens and discover their signature foods.
            </p>
          </div>
          <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill fs-6">
            {isPending ? "Loading..." : `${total} Kitchens`}
          </Badge>
        </div>
      </div>

      <div className="bg-light rounded-4 p-3 p-md-4 mb-4">
        <Container>
          <Row>
            {isPending
              ? Array(8)
                  .fill(0)
                  .map((_, index) => (
                    <Col
                      xl={3}
                      lg={4}
                      md={6}
                      sm={12}
                      key={index}
                      className="mb-3"
                    >
                      <Skeleton height={300} width={"100%"} />
                    </Col>
                  ))
              : data?.map((item, index) => (
                  <Col
                    xl={3}
                    lg={4}
                    md={6}
                    sm={12}
                    key={item?._id || index}
                    className="mb-3"
                  >
                    <KitchenCard item={item} />
                  </Col>
                ))}
          </Row>
        </Container>

        {!isPending && (
          <LoadMore
            hasMore={hasNextPage}
            isLoading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            label="Load more kitchens"
          />
        )}
      </div>
    </div>
  );
}
